import { useMemo } from 'react';
import {
  Route,
  Routes,
  UNSAFE_LocationContext as LocationContext,
  UNSAFE_NavigationContext as NavigationContext,
  UNSAFE_RouteContext as RouteContext,
  createPath,
  parsePath,
  type Location,
  type NavigationType,
  type Navigator,
  type To,
} from 'react-router-dom';
import { useEngine } from '../../api/context';
import { moduleRoutes } from '../../router';
import { EmptyState } from '../../design/primitives/Misc';
import { useWindows, type WindowState } from './WindowManager';

// A window renders one route path's screen. React Router forbids a nested <Router>, so a
// window is instead a ROUTING SCOPE: it overrides the location and navigation contexts the
// hooks read (useLocation / useNavigate / useSearchParams / <Link>) so that everything a
// screen does inside the window — open a record, switch a tab, change a filter, the `?new=1`
// drawer convention — navigates the WINDOW's own path and history, never the base pane. The
// engine providers (client, meta, inspector, toasts) live above the Desktop skin, so the
// screens work unchanged.

function toPath(to: To, current: Location): string {
  if (typeof to === 'string') {
    if (to.startsWith('?')) return createPath({ pathname: current.pathname, search: to, hash: '' });
    if (to.startsWith('#')) return createPath({ pathname: current.pathname, search: current.search, hash: to });
    return to;
  }
  return createPath({ pathname: to.pathname ?? current.pathname, search: to.search ?? '', hash: to.hash ?? '' });
}

export function WindowContent({ win }: { win: WindowState }) {
  const { modules } = useEngine();
  const wm = useWindows();
  const location = useMemo<Location>(() => {
    const p = parsePath(win.path);
    return { pathname: p.pathname ?? '/', search: p.search ?? '', hash: p.hash ?? '', state: null, key: `${win.id}:${win.index ?? 0}` };
  }, [win.path, win.id, win.index]);
  const navigator = useMemo<Navigator>(
    () => ({
      createHref: (to) => toPath(to, location),
      go: (delta) => wm.go(win.id, delta),
      push: (to) => wm.navigate(win.id, toPath(to, location)),
      replace: (to) => wm.navigate(win.id, toPath(to, location), { replace: true }),
    }),
    [wm, win.id, location],
  );
  const navigation = useMemo(() => ({ basename: '/', navigator, static: false, useTransitions: false, future: { v7_relativeSplatPath: true } }), [navigator]);
  const loc = useMemo(() => ({ location, navigationType: 'PUSH' as NavigationType }), [location]);
  const routeCtx = useMemo(() => ({ outlet: null, matches: [], isDataRoute: false }), []);
  return (
    <NavigationContext.Provider value={navigation}>
      <LocationContext.Provider value={loc}>
        <RouteContext.Provider value={routeCtx}>
          <Routes>
            {moduleRoutes(modules)}
            <Route path="*" element={<div className="hf-page"><EmptyState title="Nothing here" hint={win.path} icon="help-circle" /></div>} />
          </Routes>
        </RouteContext.Provider>
      </LocationContext.Provider>
    </NavigationContext.Provider>
  );
}
